'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { LoginRootWrapper } from './login/components/LoginRootWrapper';
import { isLoginPathname } from '@/lib/utils/loginPath';
import { runAfterPaintedFrame } from '@/app/shop/components/coverDismissWaits';

// Isolated from the login boundary module so evaluating LoginRootWrapper
// does not register a dynamic() factory that prefetches shared-ui.
const ClientWrapperInner = dynamic(
    () => import('./ClientWrapper').then((m) => ({ default: m.ClientWrapper })),
    { ssr: false },
);

/** Background tab — rAF never fires there, so don't hold the shop forever. */
const FIRST_PAINT_FALLBACK_MS = 1_500;

/**
 * True after the browser has painted one frame (double rAF), i.e. once the
 * SSR loader in the root layout is on screen.
 *
 * The heavy shell must wait for that paint rather than mount immediately: the
 * shared-ui template chunk (~1MB gz) would otherwise download and evaluate
 * ahead of the cover's own paint, competing with it for bandwidth and then
 * for the main thread. Landing it before the first paint also makes the
 * throttled lab reports count it as part of the store's load time (staging
 * king-candy: 624ms measured on the device, 9.5s in the simulation).
 */
function useAfterFirstPaintedFrame(): boolean {
    const [afterFirstPaint, setAfterFirstPaint] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const done = () => {
            if (!cancelled) {
                setAfterFirstPaint(true);
            }
        };
        const cancelPaintWait = runAfterPaintedFrame(done);
        const fallback = setTimeout(done, FIRST_PAINT_FALLBACK_MS);

        return () => {
            cancelled = true;
            cancelPaintWait();
            clearTimeout(fallback);
        };
    }, []);

    return afterFirstPaint;
}

export function ShopClientWrapper({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement | null {
    const pathname = usePathname();
    const lastPathRef = useRef<string | null>(pathname);
    const afterFirstPaint = useAfterFirstPaintedFrame();
    if (pathname != null) {
        lastPathRef.current = pathname;
    }
    const effectivePath = pathname ?? lastPathRef.current;

    // Client navigations from /shop → /login should switch shells without a
    // full document reload. Stick to the last known path when pathname is null
    // so we do not briefly remount the wrong providers.
    if (isLoginPathname(effectivePath)) {
        return <LoginRootWrapper>{children}</LoginRootWrapper>;
    }

    // The SSR loader (root layout) is on screen; nothing visible is lost by
    // holding the ssr:false shell until it has painted.
    if (!afterFirstPaint) {
        return null;
    }

    return <ClientWrapperInner>{children}</ClientWrapperInner>;
}
